import { isAdminPosition } from "../access/positions";
import { useAccess } from "../access/AccessContext";
// Admin Dashboard — live warehouse activity for planning.
//
// One filter row of warehouse activities (Goods In, Putaway, Pick Face,
// Sorting, Dispatch); Pick Face and Sorting are live today, the rest
// are greyed out until their views are built. Each activity shows two
// tables: the work happening right now (with an estimated completion
// time from the operator's recent scan speed), and beneath it the same
// work COMPLETED TODAY — same columns, but a real end time instead of
// the estimate. All numbers are computed server-side over
// /admin/dashboard/*.
//
// Waves and Summary share a date filter (Alex, 2026-09-17): Today by
// default; any other window asks the same endpoints for ?from&to and
// shows the work completed in it. The live tables stay only while
// today is inside the window — a closed window is fetched once and
// not polled.
//
// Both activities poll together every 10s (paused while the tab is
// hidden) so the pills always carry live counts and switching pills is
// instant. Poll failures keep the last-good rows — no flicker on a blip.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Calculator, Trophy, User, Users, Waves } from "lucide-react";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import {
    fetchPickingActivity, fetchSortingActivity, fetchShelfDropActivity, fetchForecastDropActivity,
    fetchGoodsInActivity, fetchDispatchActivity, fetchPutAwayActivity, fetchTeamOverview,
    fetchTeamKpis, EMPTY_TEAM_KPIS, TeamKpis,
    fetchTeamWeek, EMPTY_TEAM_WEEK, TeamWeek,
    PickingActivity, SortingActivity, ShelfDropActivity, GoodsInActivity, DispatchActivity,
    PutAwayActivity, PutAwayType, LivePutAwayRow, CompletedPutAwayRow,
    TeamOverview,
    EMPTY_PICKING, EMPTY_SORTING, EMPTY_SHELF_DROP, EMPTY_GOODS_IN, EMPTY_DISPATCH, EMPTY_PUTAWAY,
    EMPTY_TEAM,
    LivePickRow, LiveSortRow, CompletedPickRow, CompletedSortRow,
    LiveShelfDropRow, CompletedShelfDropRow,
    LiveGoodsInRow, CompletedGoodsInRow, LiveDispatchRow, CompletedDispatchRow, DispatchPickType,
    PickingByUser, SortingByUser, GoodsInByUser, ShelfDropByUser, PutAwayByUser, DispatchByUser,
    SorterActivity, SorterBatchRow, SorterByUser, PalletsCreatedActivity, PalletCreatedRow, PalletsByUser,
    EMPTY_SORTER, EMPTY_PALLETS_CREATED,
    fetchSorter1Activity, fetchSorter2Activity, fetchPalletsCreatedActivity,
} from "../api/dashboardActivity";
import DashboardTeam from "./DashboardTeam";
import DashboardPlanner from "./DashboardPlanner";
import DashboardLeadersBonus from "./DashboardLeadersBonus";
import DashboardShippedByDate from "./DashboardShippedByDate";
import { KpiPreset, KpiRange, clampSpan, presetRange, todayIso } from "./helpers/kpiRange";
import { loadSnapshot, saveSnapshot, updatedAgo } from "./helpers/dashboardSnapshot";
import { ColumnSpec, SortState, nextSort, sortRows, timeMs } from "./helpers/dashboardSort";
import { DashboardLayout, dashboardLayoutOf, dashboardWarehouseShown, showsShippedByDate } from "./helpers/dashboardLayout";
import { batchDateLabel } from "./DashboardSorterRace";
import "../styles/admin-dashboard.css";

type Activity = "goods_in" | "putaway" | "pick_face" | "forecast_drop" | "shelf_drop" | "sorting" | "dispatch"
    | "sorter_1" | "sorter_2" | "pallets_created";

// Team = the "Daily output" page of per-department summary metrics +
// hourly charts (GET /admin/dashboard/team), rendered by DashboardTeam.
// Waves = today's live + completed activity tables (one row per wave /
// session / stint). Individual = the same day's live + completed work
// folded to one line per person per department (the payloads' by_user
// arrays), behind the same department pills — Waves and Individual
// share the same five polls. Planner = the staffing calculator
// (DashboardPlanner) — no polling of its own beyond one Team fetch.
// leaders_bonus = the pay-period KPI view (DashboardLeadersBonus, Alex
// 2026-09-28) — its own polling, none of the activity/Team fetches.
type Scope = "team" | "waves" | "individual" | "planner" | "leaders_bonus";

const KPI_PRESETS: KpiPreset[] = ["this_week", "wtd", "last_week", "mtd", "custom"];
// Waves / Summary history filter — Today first, the default.
const ACTIVITY_PRESETS: KpiPreset[] = ["today", "yesterday", "this_week", "wtd", "last_week", "mtd", "custom"];
const PRESET_LABEL_KEY: Record<KpiPreset, string> = {
    today: "dashboard_team_range_today",
    yesterday: "dashboard_team_range_yesterday",
    this_week: "dashboard_team_range_this_week",
    wtd: "dashboard_team_range_wtd",
    last_week: "dashboard_team_range_last_week",
    mtd: "dashboard_team_range_mtd",
    custom: "dashboard_team_range_custom",
};

type ActivityPill = { key: Activity; labelKey: string; enabled: boolean };
const ACTIVITIES: ActivityPill[] = [
    { key: "goods_in",   labelKey: "goods_in",           enabled: true },
    { key: "putaway",    labelKey: "put_away",           enabled: true },
    { key: "pick_face",  labelKey: "picking_admin_title", enabled: true },
    // Forecast Drop sits before Shelf Drop, matching the Team card order.
    { key: "forecast_drop", labelKey: "forecast_drop",   enabled: true },
    { key: "shelf_drop", labelKey: "shelf_drop",         enabled: true },
    { key: "sorting",    labelKey: "zone_storage",       enabled: true },
    { key: "dispatch",   labelKey: "dispatch",           enabled: true },
];
// Newport / Poland (sorter layout, 2026-10-20): the pills match the Team
// cards — no picking, drops or P&S sorting there.
const SORTER_ACTIVITIES: ActivityPill[] = [
    { key: "goods_in",   labelKey: "goods_in",   enabled: true },
    { key: "putaway",    labelKey: "put_away",   enabled: true },
    { key: "sorter_1",   labelKey: "sorter_1",   enabled: true },
    { key: "sorter_2",   labelKey: "sorter_2",   enabled: true },
    { key: "dispatch",   labelKey: "dispatch",   enabled: true },
    { key: "pallets_created", labelKey: "pallets_created", enabled: true },
];
const activitiesFor = (layout: DashboardLayout): ActivityPill[] =>
    layout === "sorter" ? SORTER_ACTIVITIES : ACTIVITIES;
const defaultActivityFor = (layout: DashboardLayout): Activity =>
    layout === "sorter" ? "sorter_1" : "pick_face";

const POLL_MS = 10_000;
// Team page (summary cards + race) refreshes more slowly than the live
// Waves/Individual tables — 30 s (Alex, 2026-09-09; was 10 s).
const TEAM_POLL_MS = 30_000;
// KPI cards span days, not minutes — refresh them once a minute.
const KPI_POLL_MS = 60_000;

// Warehouse scoping (mirrors the BE's dashboardScope): superAdmins get
// the full warehouse dropdown defaulting to "All"; everyone else is
// locked to their own warehouse — the BE ignores any override they
// send, so the FE shows a static label instead of a dead dropdown.
const sessionUser = (): { permissions: string | null; position: string | null; warehouse: string | null } => {
    try {
        const raw = window.localStorage.getItem("user");
        if (raw) {
            const u = JSON.parse(raw);
            return { permissions: u.permissions ?? null, position: u.position ?? null, warehouse: u.warehouse ?? null };
        }
    } catch { /* fallthrough */ }
    return { permissions: null, position: null, warehouse: null };
};

const formatClock = (iso: string | null) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "—";
    }
};

// Start/end cells over a multi-day window: the day matters as much as
// the time.
const formatDateClock = (iso: string | null) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch {
        return "—";
    }
};

// "17/09/2026" from a YYYY-MM-DD, in the viewer's locale.
const formatIsoDate = (isoDate: string) => {
    const [y, m, d] = isoDate.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString();
};

const DashboardAdmin = () => {
    const { t } = useTranslation();
    const [user] = useState(sessionUser);
    // Who gets the warehouse dropdown: the `cross_warehouse` grant (D14, read or write) once the
    // access map is authoritative; before that (or outside a provider) the old Admin-only rule.
    // Admin holds every key, so nothing changes for them; a Manager or anyone granted the key in
    // the matrix now gets the dropdown too — the BE honours the same grant on ?warehouse=.
    const { authoritative, hasAccess } = useAccess();
    const isAdmin = isAdminPosition(user.position ?? user.permissions);
    const isSuperAdmin = authoritative ? hasAccess("cross_warehouse") : isAdmin;
    // Team is the landing view (per Alex, 2026-08-24).
    const [scope, setScope] = useState<Scope>("team");
    // Everyone lands on their OWN warehouse (Alex, 2026-10-20 — Admin used
    // to land on "All"); the dropdown still offers All warehouses.
    const [warehouseFilter, setWarehouseFilter] = useState<string>(user.warehouse ?? "");
    // Which Team layout / pill set the filtered warehouse gets.
    const layout = dashboardLayoutOf(warehouseFilter);
    const activities = useMemo(() => activitiesFor(layout), [layout]);
    const [activity, setActivity] = useState<Activity>(() => defaultActivityFor(dashboardLayoutOf(user.warehouse ?? "")));
    // A pill that the new layout does not offer falls back to its default.
    useEffect(() => {
        if (!activities.some((a) => a.key === activity)) setActivity(defaultActivityFor(layout));
    }, [activities, activity, layout]);
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [picks, setPicks] = useState<PickingActivity>(EMPTY_PICKING);
    const [drops, setDrops] = useState<ShelfDropActivity>(EMPTY_SHELF_DROP);
    const [forecast, setForecast] = useState<ShelfDropActivity>(EMPTY_SHELF_DROP);
    const [sorts, setSorts] = useState<SortingActivity>(EMPTY_SORTING);
    const [goodsIn, setGoodsIn] = useState<GoodsInActivity>(EMPTY_GOODS_IN);
    const [putaway, setPutaway] = useState<PutAwayActivity>(EMPTY_PUTAWAY);
    const [dispatch, setDispatch] = useState<DispatchActivity>(EMPTY_DISPATCH);
    const [sorter1, setSorter1] = useState<SorterActivity>(EMPTY_SORTER);
    const [sorter2, setSorter2] = useState<SorterActivity>(EMPTY_SORTER);
    const [pallets, setPallets] = useState<PalletsCreatedActivity>(EMPTY_PALLETS_CREATED);
    const [loaded, setLoaded] = useState(false);
    // Waves / Summary date window. "today" is the live default and is
    // sent as NO range (the BE resolves today itself, so a tab left
    // open past midnight rolls over); everything else is fixed dates.
    const [rangePreset, setRangePreset] = useState<KpiPreset>("today");
    const [range, setRange] = useState<KpiRange>(() => presetRange("today"));
    const today = todayIso();
    const rangeIsToday = rangePreset === "today";
    // `to` past today holds nothing — the BE clips it, the labels do too.
    const rangeTo = range.to > today ? today : range.to;
    const rangeIncludesToday = rangeIsToday || (range.from <= today && rangeTo >= today);
    const rangeSpansDays = !rangeIsToday && range.from !== rangeTo;
    const rangeLabel = range.from === rangeTo
        ? formatIsoDate(range.from)
        : `${formatIsoDate(range.from)} – ${formatIsoDate(rangeTo)}`;
    // Column sort per table (Alex, 2026-09-10) — keyed
    // `<activity>:live|completed|summary` so each pill's tables keep
    // their own sort while you switch around. No entry = BE order.
    const [colSorts, setColSorts] = useState<Record<string, SortState>>({});
    const [team, setTeam] = useState<TeamOverview>(EMPTY_TEAM);
    const [teamLoaded, setTeamLoaded] = useState(false);
    // Team KPI cards (on-time rate, time to ship) run over a date
    // range — this week (Mon → Sat) by default — on a slower poll than
    // the live figures.
    const [kpiPreset, setKpiPreset] = useState<KpiPreset>("this_week");
    const [kpiRange, setKpiRange] = useState<KpiRange>(() => presetRange("this_week"));
    const [kpis, setKpis] = useState<TeamKpis>(EMPTY_TEAM_KPIS);
    const [kpisLoaded, setKpisLoaded] = useState(false);
    // Weekly KPIs (Mon→Sat consolidation of the daily section) — same
    // slow poll as the range KPIs.
    const [week, setWeek] = useState<TeamWeek>(EMPTY_TEAM_WEEK);
    const [weekLoaded, setWeekLoaded] = useState(false);

    // Warehouses for the filter dropdown (same source as PickingAdmin).
    // Locked (non-superAdmin) viewers have no dropdown to fill.
    useEffect(() => {
        if (!isSuperAdmin) return undefined;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch("/settings", {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && Array.isArray(data)) {
                    // Test warehouses (name contains "TEST") are never
                    // offered — the BE excludes them from "all" too — and
                    // nor is MCL (a 3PL; Alex, 2026-10-20).
                    setWarehouses(data.map((r: { warehouse: string }) => r.warehouse)
                        .filter(dashboardWarehouseShown).sort());
                }
            } catch { /* leave empty */ }
        })();
        return () => { alive = false; };
    }, [isSuperAdmin]);

    const loadAll = useCallback(async () => {
        const q = rangeIsToday ? undefined : range;
        try {
            if (layout === "sorter") {
                const [g, pa, s1, s2, d, pc] = await Promise.all([
                    fetchGoodsInActivity(warehouseFilter, q),
                    fetchPutAwayActivity(warehouseFilter, q),
                    fetchSorter1Activity(warehouseFilter, q),
                    fetchSorter2Activity(warehouseFilter, q),
                    fetchDispatchActivity(warehouseFilter, q),
                    fetchPalletsCreatedActivity(warehouseFilter, q),
                ]);
                setGoodsIn(g);
                setPutaway(pa);
                setSorter1(s1);
                setSorter2(s2);
                setDispatch(d);
                setPallets(pc);
                setLoaded(true);
                return;
            }
            const [p, sd, s, g, d, pa, fd] = await Promise.all([
                fetchPickingActivity(warehouseFilter, q),
                fetchShelfDropActivity(warehouseFilter, q),
                fetchSortingActivity(warehouseFilter, q),
                fetchGoodsInActivity(warehouseFilter, q),
                fetchDispatchActivity(warehouseFilter, q),
                fetchPutAwayActivity(warehouseFilter, q),
                fetchForecastDropActivity(warehouseFilter, q),
            ]);
            setPicks(p);
            setDrops(sd);
            setForecast(fd);
            setSorts(s);
            setGoodsIn(g);
            setDispatch(d);
            setPutaway(pa);
            setLoaded(true);
        } catch {
            toast.error(t("server_error"), { toastId: "dashboard-admin-load-err" });
        }
    }, [warehouseFilter, layout, rangeIsToday, range, t]);

    // A window that ends before today never changes: one fetch, no poll.
    useEffect(() => {
        if (scope === "team" || scope === "planner" || scope === "leaders_bonus") return undefined;
        let alive = true;
        setLoaded(false);
        loadAll();
        if (!rangeIncludesToday) return () => { alive = false; };
        const tick = async () => {
            if (!alive || document.visibilityState === "hidden") return;
            await loadAll();
        };
        const id = window.setInterval(tick, POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") tick();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            alive = false;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [scope, loadAll, rangeIncludesToday]);

    // Team view polls its own aggregate endpoint on the same cadence,
    // only while the Team toggle is active.
    const loadTeam = useCallback(async () => {
        try {
            const payload = await fetchTeamOverview(warehouseFilter);
            setTeam(payload);
            setTeamLoaded(true);
            saveSnapshot("team", warehouseFilter || "all", payload);
        } catch {
            toast.error(t("server_error"), { toastId: "dashboard-admin-load-err" });
        }
    }, [warehouseFilter, t]);

    useEffect(() => {
        if (scope !== "team") return undefined;
        let alive = true;
        // Paint the last payload for this scope straight away (the BE
        // snapshot refreshes it within one poll); zeros only when there
        // has never been one this session.
        // Merged over EMPTY_TEAM: a snapshot saved by an older FE build
        // lacks any block added since (the race card crashed on one,
        // 2026-09-09), and the defaults fill those in until the next poll.
        const restored = loadSnapshot<TeamOverview>("team", warehouseFilter || "all");
        if (restored) { setTeam({ ...EMPTY_TEAM, ...restored }); setTeamLoaded(true); } else { setTeamLoaded(false); }
        const tick = async () => {
            if (!alive || document.visibilityState === "hidden") return;
            await loadTeam();
        };
        loadTeam();
        const id = window.setInterval(tick, TEAM_POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") tick();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            alive = false;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [scope, loadTeam, warehouseFilter]);

    const loadKpis = useCallback(async () => {
        try {
            const payload = await fetchTeamKpis(warehouseFilter, kpiRange.from, kpiRange.to);
            setKpis(payload);
            setKpisLoaded(true);
            saveSnapshot("kpis", `${warehouseFilter || "all"}:${kpiRange.from}:${kpiRange.to}`, payload);
        } catch {
            toast.error(t("server_error"), { toastId: "dashboard-admin-load-err" });
        }
    }, [warehouseFilter, kpiRange, t]);

    const loadWeek = useCallback(async () => {
        try {
            const payload = await fetchTeamWeek(warehouseFilter);
            setWeek(payload);
            setWeekLoaded(true);
            saveSnapshot("week", warehouseFilter || "all", payload);
        } catch {
            toast.error(t("server_error"), { toastId: "dashboard-admin-load-err" });
        }
    }, [warehouseFilter, t]);

    useEffect(() => {
        if (scope !== "team") return undefined;
        let alive = true;
        const restored = loadSnapshot<TeamWeek>("week", warehouseFilter || "all");
        if (restored) { setWeek({ ...EMPTY_TEAM_WEEK, ...restored }); setWeekLoaded(true); } else { setWeekLoaded(false); }
        const tick = async () => {
            if (!alive || document.visibilityState === "hidden") return;
            await loadWeek();
        };
        loadWeek();
        const id = window.setInterval(tick, KPI_POLL_MS);
        return () => {
            alive = false;
            window.clearInterval(id);
        };
    }, [scope, loadWeek, warehouseFilter]);

    useEffect(() => {
        if (scope !== "team") return undefined;
        let alive = true;
        const restored = loadSnapshot<TeamKpis>("kpis", `${warehouseFilter || "all"}:${kpiRange.from}:${kpiRange.to}`);
        if (restored) { setKpis({ ...EMPTY_TEAM_KPIS, ...restored }); setKpisLoaded(true); } else { setKpisLoaded(false); }
        const tick = async () => {
            if (!alive || document.visibilityState === "hidden") return;
            await loadKpis();
        };
        loadKpis();
        const id = window.setInterval(tick, KPI_POLL_MS);
        return () => {
            alive = false;
            window.clearInterval(id);
        };
    }, [scope, loadKpis, warehouseFilter, kpiRange]);

    // "Updated Ns ago" — age of the BE snapshot the Team figures come
    // from, re-rendered every few seconds while the Team view is open.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        if (scope !== "team") return undefined;
        setNowMs(Date.now());
        const id = window.setInterval(() => setNowMs(Date.now()), 5_000);
        return () => window.clearInterval(id);
    }, [scope]);
    const ago = updatedAgo(team.snapshot_at, nowMs);
    const updatedLabel = ago === null
        ? null
        : "secs" in ago
            ? t("dashboard_updated_secs", { n: String(ago.secs) })
            : t("dashboard_updated_mins", { n: String(ago.mins) });

    const onKpiPreset = (preset: KpiPreset) => {
        setKpiPreset(preset);
        if (preset !== "custom") setKpiRange(presetRange(preset));
    };
    const onKpiRange = (next: KpiRange) => {
        setKpiPreset("custom");
        setKpiRange(next);
    };
    const onRangePreset = (preset: KpiPreset) => {
        setRangePreset(preset);
        if (preset !== "custom") setRange(presetRange(preset));
    };
    const onRangeChange = (next: KpiRange, edited: "from" | "to") => {
        setRangePreset("custom");
        setRange(clampSpan(next, edited));
    };

    const payloadFor = (key: Activity) => {
        if (key === "pick_face") return picks;
        if (key === "shelf_drop") return drops;
        if (key === "forecast_drop") return forecast;
        if (key === "sorting") return sorts;
        if (key === "goods_in") return goodsIn;
        if (key === "dispatch") return dispatch;
        if (key === "sorter_1") return sorter1;
        if (key === "sorter_2") return sorter2;
        if (key === "pallets_created") return pallets;
        return putaway;
    };
    // Waves pills count what the window shows: live rows while today is
    // in it, completed rows for a closed window.
    const countFor = (key: Activity): number | null => {
        const data = payloadFor(key);
        return rangeIncludesToday ? data.live.length : data.completed.length;
    };
    // Start/end cells carry the date once the window spans days.
    const clock = rangeSpansDays ? formatDateClock : formatClock;

    // Shared cells. Paused rows suppress the ETA (nobody is walking);
    // idle rows keep it but dimmed — the speed it's built on is going
    // stale.
    const progressCell = (done: number, total: number, pct: number) => (
        <td data-label={t("dashboard_col_progress")}>
            <div className="dash-progress-wrap">
                <span className="dash-progress-text">
                    {done} / {total} ({pct}%)
                </span>
                <div className="dash-progress">
                    <div className="dash-progress-fill" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
            </div>
        </td>
    );

    // BE reports items/min; the floor shows items/hour.
    const speedCell = (speedIpm: number | null) => (
        <td data-label={t("dashboard_col_speed")}>
            {speedIpm !== null ? t("dashboard_speed_iph", { n: String(Math.round(speedIpm * 60)) }) : "—"}
        </td>
    );

    const startCell = (iso: string | null) => (
        <td data-label={t("dashboard_col_start")}>{clock(iso)}</td>
    );

    const endCell = (iso: string) => (
        <td data-label={t("dashboard_col_end")}>{clock(iso)}</td>
    );

    const etaCell = (row: LivePickRow | LiveSortRow, paused: boolean) => {
        if (paused) {
            return (
                <td data-label={t("dashboard_col_eta")}>
                    <span className="dash-badge dash-badge--paused">{t("dashboard_paused")}</span>
                </td>
            );
        }
        const eta = row.eta_minutes !== null && row.eta_at !== null
            ? t("dashboard_eta_in", { mins: String(row.eta_minutes), time: formatClock(row.eta_at) })
            : "—";
        return (
            <td data-label={t("dashboard_col_eta")} className="dash-eta-cell">
                {row.idle && <span className="dash-badge dash-badge--idle">{t("dashboard_idle")}</span>}
                <span className={row.idle ? "dash-eta--stale" : undefined}>{eta}</span>
            </td>
        );
    };

    const emptyState = (key: string) => (
        <div className="admin-settings-empty">{loaded ? t(key) : t("loading")}</div>
    );

    // ── Sortable headers (Waves + Summary) ───────────────────────────
    // Each table declares its columns as ColumnSpecs (header key, how
    // the value compares, how to read it); the header renders from the
    // spec and the body rows go through `sorted` with the same spec, so
    // the two can't drift. Cells stay hand-written in the same order.
    const liveTable = `${activity}:live`;
    const doneTable = `${activity}:completed`;
    const summaryTableId = `${activity}:summary`;
    const toggleSort = (tableId: string, col: ColumnSpec<never>) =>
        setColSorts((prev) => ({ ...prev, [tableId]: nextSort(prev[tableId], col) }));
    const sorted = <R,>(tableId: string, rows: R[], cols: ColumnSpec<R>[]) =>
        sortRows(rows, cols, colSorts[tableId]);
    const sortableHead = <R,>(tableId: string, cols: ColumnSpec<R>[]) => {
        const st = colSorts[tableId];
        return (
            <thead>
                <tr>
                    {cols.map((c) => {
                        const dir = st && st.col === c.key ? st.dir : null;
                        return (
                            <th
                                key={c.key}
                                className={c.className}
                                aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
                            >
                                <button
                                    type="button"
                                    className={`dash-sort-btn${dir ? " dash-sort-btn--active" : ""}`}
                                    title={t("dashboard_sort_hint")}
                                    onClick={() => toggleSort(tableId, c)}
                                >
                                    <span>{t(c.key)}</span>
                                    <span className="dash-sort-arrow" aria-hidden="true">
                                        {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
                                    </span>
                                </button>
                            </th>
                        );
                    })}
                </tr>
            </thead>
        );
    };
    // Last-column specs shared by the live / completed tables.
    const etaCol: ColumnSpec<LivePickRow | LiveSortRow> = {
        key: "dashboard_col_eta", kind: "number", value: (r) => r.eta_minutes,
    };
    const endCol = <R extends { ended_at: string }>(): ColumnSpec<R> => ({
        key: "dashboard_col_end", kind: "number", value: (r) => timeMs(r.ended_at),
    });
    const lastActivityCol = <R,>(at: (r: R) => string | null): ColumnSpec<R> => ({
        key: "dashboard_col_last_activity", kind: "number", value: (r) => timeMs(at(r)),
    });
    const startCol = <R extends { started_at: string | null }>(className?: string): ColumnSpec<R> => ({
        key: "dashboard_col_start", kind: "number", value: (r) => timeMs(r.started_at), className,
    });
    const warehouseCol = <R extends { warehouse: string | null }>(className?: string): ColumnSpec<R> => ({
        key: "warehouse_col", kind: "text", value: (r) => r.warehouse, className,
    });

    type PickRow = LivePickRow | CompletedPickRow;
    // A Putwall wave is a Pick & Sort wave sorted at the put wall — it
    // gets its own Type label so the two read apart on the Waves tab.
    const pickTypeLabel = (w: PickRow) =>
        w.pick_type === "pick_and_sort" && w.sort_station === "put_wall"
            ? t("picking_pick_type_put_wall")
            : t(`picking_pick_type_${w.pick_type}`);
    // Wave / Order (Alex, 2026-10-16): a Large wave is one order at a
    // time, so its row names the order — live: the one being picked
    // now; completed: every order the wave held. Single and Pick &
    // Sort rows keep the wave id. Order ids carry a channel prefix
    // (bargainfox:123, consol:bargainfox:123) — the column shows just
    // the number after the last colon (Alex, 2026-10-29).
    const WAVE_ORDER_SHOWN = 2;
    const orderNumber = (id: string): string => id.slice(id.lastIndexOf(":") + 1);
    const waveOrderText = (w: PickRow): string => {
        if (w.pick_type !== "large") return `#${w.wave_id}`;
        if ("current_order_id" in w) return w.current_order_id ? orderNumber(w.current_order_id) : `#${w.wave_id}`;
        return w.order_ids.length > 0 ? w.order_ids.map(orderNumber).join(", ") : `#${w.wave_id}`;
    };
    const waveOrderCell = (w: PickRow) => {
        const text = waveOrderText(w);
        const all = w.pick_type === "large" && !("current_order_id" in w) ? w.order_ids.map(orderNumber) : [];
        const extra = all.length - WAVE_ORDER_SHOWN;
        return (
            <td data-label={t("dashboard_col_wave_order")} title={extra > 0 ? text : undefined}>
                {extra > 0
                    ? `${all.slice(0, WAVE_ORDER_SHOWN).join(", ")} ${t("dashboard_wave_order_more", { n: extra })}`
                    : text}
            </td>
        );
    };
    const pickCols = <R extends PickRow>(last: ColumnSpec<R>): ColumnSpec<R>[] => [
        { key: "dashboard_col_user", kind: "text", value: (w) => w.picker_name, className: "dash-th-user" },
        { key: "picking_admin_pick_type_col", kind: "text", value: (w) => pickTypeLabel(w), className: "dash-th-md" },
        { key: "picking_admin_location_col", kind: "text", value: (w) => w.location, className: "dash-th-md" },
        { key: "dashboard_col_wave_order", kind: "text", value: (w) => waveOrderText(w), className: "dash-th-md" },
        warehouseCol("dash-th-sm"),
        startCol("dash-th-sm"),
        { key: "dashboard_col_progress", kind: "number", value: (w) => w.pct, className: "dash-th-progress" },
        { key: "dashboard_col_speed", kind: "number", value: (w) => w.speed_ipm, className: "dash-th-md" },
        { ...last, className: "dash-th-eta" },
    ];
    const pickLiveCols = pickCols<LivePickRow>(etaCol);
    const pickDoneCols = pickCols<CompletedPickRow>(endCol());

    const pickCells = (w: LivePickRow | CompletedPickRow) => (
        <>
            <td data-label={t("dashboard_col_user")} className="dash-td-primary">
                {w.picker_name ?? "—"}
                {w.took_over && (
                    <span className="dash-badge dash-badge--takenover">{t("dashboard_taken_over")}</span>
                )}
                {"handed_over" in w && w.handed_over && (
                    <span className="dash-badge dash-badge--passed">{t("dashboard_passed_on")}</span>
                )}
            </td>
            <td data-label={t("picking_admin_pick_type_col")}>{pickTypeLabel(w)}</td>
            <td data-label={t("picking_admin_location_col")}>{w.location ?? "—"}</td>
            {waveOrderCell(w)}
            <td data-label={t("warehouse_col")}>{w.warehouse}</td>
            {startCell(w.started_at)}
            {progressCell(w.picked_items, w.total_items, w.pct)}
            {speedCell(w.speed_ipm)}
        </>
    );

    type SortRow = LiveSortRow | CompletedSortRow;
    const sortCols = <R extends SortRow>(last: ColumnSpec<R>): ColumnSpec<R>[] => [
        { key: "dashboard_col_user", kind: "text", value: (s) => s.sorter_name, className: "dash-th-user" },
        { key: "dashboard_col_bay", kind: "text", value: (s) => s.bay_names.join(" + "), className: "dash-th-md" },
        { key: "picking_wave_id_col", kind: "number", value: (s) => Number(s.wave_id), className: "dash-th-sm" },
        warehouseCol("dash-th-sm"),
        { key: "picking_admin_orders_col", kind: "number", value: (s) => s.orders_sorted, className: "dash-th-md" },
        { key: "dashboard_col_start", kind: "number", value: (s) => timeMs(s.entered_at), className: "dash-th-sm" },
        { key: "dashboard_col_progress", kind: "number", value: (s) => s.pct, className: "dash-th-progress" },
        { key: "dashboard_col_speed", kind: "number", value: (s) => s.speed_ipm, className: "dash-th-md" },
        { ...last, className: "dash-th-eta" },
    ];
    const sortLiveCols = sortCols<LiveSortRow>(etaCol);
    const sortDoneCols = sortCols<CompletedSortRow>(endCol());

    const sortCells = (s: LiveSortRow | CompletedSortRow) => (
        <>
            <td data-label={t("dashboard_col_user")} className="dash-td-primary">
                {s.sorter_name ?? "—"}
                {s.took_over && (
                    <span className="dash-badge dash-badge--takenover">{t("dashboard_taken_over")}</span>
                )}
                {"handed_over" in s && s.handed_over && (
                    <span className="dash-badge dash-badge--passed">{t("dashboard_passed_on")}</span>
                )}
            </td>
            <td data-label={t("dashboard_col_bay")}>{s.bay_names.length > 0 ? s.bay_names.join(" + ") : "—"}</td>
            <td data-label={t("picking_wave_id_col")}>#{s.wave_id}</td>
            <td data-label={t("warehouse_col")}>{s.warehouse}</td>
            <td data-label={t("picking_admin_orders_col")}>{s.orders_sorted} / {s.orders_total}</td>
            {startCell(s.entered_at)}
            {progressCell(s.sorted_items, s.total_items, s.pct)}
            {speedCell(s.speed_ipm)}
        </>
    );

    const liveSection = (emptyKey: string, count: number, table: React.ReactNode) =>
        !rangeIncludesToday ? null : count === 0 ? emptyState(emptyKey) : table;

    const completedSection = (count: number, table: React.ReactNode) => (
        <div className="dash-completed-section">
            <h3 className="dash-completed-heading">
                {rangeIsToday ? t("dashboard_completed_today") : t("dashboard_completed_range", { range: rangeLabel })}
                {loaded ? ` (${count})` : ""}
            </h3>
            {count === 0 ? emptyState(rangeIsToday ? "dashboard_no_completed" : "dashboard_no_completed_range") : table}
        </div>
    );

    const pickingView = () => (
        <>
            {liveSection("dashboard_no_picks", picks.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, pickLiveCols)}
                        <tbody>
                            {sorted(liveTable, picks.live, pickLiveCols).map((w) => (
                                <tr key={w.wave_id}>
                                    {pickCells(w)}
                                    {etaCell(w, w.status === "paused")}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(picks.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, pickDoneCols)}
                        <tbody>
                            {sorted(doneTable, picks.completed, pickDoneCols).map((w) => (
                                <tr key={`${w.wave_id}:${w.picker_uid ?? w.picker_name ?? "?"}`}>
                                    {pickCells(w)}
                                    {endCell(w.ended_at)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    // Shelf Drop: one row per PERSON — everyone replenishes the same
    // shared list, so there is no per-row target/ETA. Units count when
    // scanned into the tote; the location is the person's latest
    // recorded one (source shelf while picking, target pick face while
    // putting away — the badge says which phase they're in).
    type DropRow = LiveShelfDropRow | CompletedShelfDropRow;
    const dropCols = <R extends DropRow>(last: ColumnSpec<R>): ColumnSpec<R>[] => [
        { key: "dashboard_col_user", kind: "text", value: (r) => r.picker_name, className: "dash-th-user" },
        { key: "picking_admin_location_col", kind: "text", value: (r) => r.location },
        warehouseCol(),
        { key: "dashboard_col_units", kind: "number", value: (r) => r.units },
        { key: "dashboard_col_speed", kind: "number", value: (r) => r.speed_ipm },
        startCol(),
        last,
    ];
    const dropLiveCols = dropCols<LiveShelfDropRow>(lastActivityCol((r) => r.last_activity_at));
    const dropDoneCols = dropCols<CompletedShelfDropRow>(endCol());

    const shelfDropCells = (r: LiveShelfDropRow | CompletedShelfDropRow) => (
        <>
            <td data-label={t("dashboard_col_user")} className="dash-td-primary">{r.picker_name ?? "—"}</td>
            <td data-label={t("picking_admin_location_col")}>
                {r.location ?? "—"}
                {"phase" in r && r.phase === "putting_away" && (
                    <span className="dash-badge dash-badge--phase">{t("dashboard_phase_putaway")}</span>
                )}
            </td>
            <td data-label={t("warehouse_col")}>{r.warehouse ?? "—"}</td>
            <td data-label={t("dashboard_col_units")}>{r.units}</td>
            {speedCell(r.speed_ipm)}
            {startCell(r.started_at)}
        </>
    );

    const shelfDropLastActivityCell = (r: LiveShelfDropRow) => (
        <td data-label={t("dashboard_col_last_activity")}>
            {r.idle && <span className="dash-badge dash-badge--idle">{t("dashboard_idle")}</span>}
            {clock(r.last_activity_at)}
        </td>
    );

    // Shared by the Shelf Drop and Forecast Drop pills — same payload
    // shape, different module (Alex, 2026-09-08).
    const dropView = (data: ShelfDropActivity, emptyKey: string) => (
        <>
            {liveSection(emptyKey, data.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, dropLiveCols)}
                        <tbody>
                            {sorted(liveTable, data.live, dropLiveCols).map((r) => (
                                <tr key={r.picker_uid ?? r.picker_name ?? "?"}>
                                    {shelfDropCells(r)}
                                    {shelfDropLastActivityCell(r)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(data.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, dropDoneCols)}
                        <tbody>
                            {sorted(doneTable, data.completed, dropDoneCols).map((r) => (
                                <tr key={`${r.picker_uid ?? r.picker_name ?? "?"}:${r.started_at}`}>
                                    {shelfDropCells(r)}
                                    {endCell(r.ended_at)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    const sortingView = () => (
        <>
            {liveSection("dashboard_no_sorts", sorts.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, sortLiveCols)}
                        <tbody>
                            {sorted(liveTable, sorts.live, sortLiveCols).map((s) => (
                                <tr key={s.wave_id}>
                                    {sortCells(s)}
                                    {etaCell(s, false)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(sorts.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, sortDoneCols)}
                        <tbody>
                            {sorted(doneTable, sorts.completed, sortDoneCols).map((s) => (
                                <tr key={`${s.wave_id}:${s.sorter_uid ?? s.sorter_name ?? "?"}`}>
                                    {sortCells(s)}
                                    {endCell(s.ended_at)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    // ── Sorter 1 / Sorter 2 (Newport, Poland — 2026-10-20): one row per
    // date+batch the station worked in the window — progress is the whole
    // batch, the rest is the window's activity. The estimate projects the
    // batch's remaining items at the row's own speed. ──
    const batchLabel = (b: SorterBatchRow) =>
        t("dashboard_team_sorter_batch", { date: batchDateLabel(b.date), n: String(b.batch ?? "—") });
    const sorterEtaMinutes = (b: SorterBatchRow): number | null => {
        const remaining = Math.max(0, b.items_total - b.items_done);
        if (b.speed_ipm === null || b.speed_ipm <= 0 || remaining === 0) return null;
        return Math.round(remaining / b.speed_ipm);
    };
    const sorterEtaText = (b: SorterBatchRow) => {
        const mins = sorterEtaMinutes(b);
        if (mins === null) return "—";
        return t("dashboard_eta_in", { mins: String(mins), time: formatClock(new Date(Date.now() + mins * 60_000).toISOString()) });
    };
    const sorterCols = (last: ColumnSpec<SorterBatchRow>): ColumnSpec<SorterBatchRow>[] => [
        { key: "dashboard_col_batch", kind: "text", value: (b) => `${b.date ?? ""}|${b.batch ?? ""}`, className: "dash-th-md" },
        warehouseCol("dash-th-sm"),
        { key: "dashboard_col_progress", kind: "number", value: (b) => b.pct },
        { key: "dashboard_col_people", kind: "number", value: (b) => b.people },
        { key: "dashboard_col_units", kind: "number", value: (b) => b.items_in_window },
        { key: "dashboard_col_speed", kind: "number", value: (b) => b.speed_ipm },
        { key: "dashboard_col_start", kind: "number", value: (b) => timeMs(b.window_started_at), className: "dash-th-sm" },
        last,
    ];
    const sorterLiveCols = sorterCols({ key: "dashboard_col_eta", kind: "number", value: (b) => sorterEtaMinutes(b) });
    const sorterDoneCols = sorterCols({ key: "dashboard_col_end", kind: "number", value: (b) => timeMs(b.ended_at ?? b.last_scan_at) });
    const sorterCells = (b: SorterBatchRow) => (
        <>
            <td data-label={t("dashboard_col_batch")}>{batchLabel(b)}</td>
            <td data-label={t("warehouse_col")}>{b.warehouse}</td>
            {progressCell(b.items_done, b.items_total, b.pct)}
            <td data-label={t("dashboard_col_people")}>{b.people}</td>
            <td data-label={t("dashboard_col_units")}>{b.items_in_window}</td>
            {speedCell(b.speed_ipm)}
            {startCell(b.window_started_at)}
        </>
    );
    const sorterView = (data: SorterActivity) => (
        <>
            {liveSection("dashboard_no_sorter_batches", data.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, sorterLiveCols)}
                        <tbody>
                            {sorted(liveTable, data.live, sorterLiveCols).map((b) => (
                                <tr key={`${b.date}|${b.batch}`}>
                                    {sorterCells(b)}
                                    <td data-label={t("dashboard_col_eta")}>{sorterEtaText(b)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(data.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, sorterDoneCols)}
                        <tbody>
                            {sorted(doneTable, data.completed, sorterDoneCols).map((b) => (
                                <tr key={`${b.date}|${b.batch}|${b.ended_at ?? ""}`}>
                                    {sorterCells(b)}
                                    <td data-label={t("dashboard_col_end")}>{clock(b.ended_at ?? b.last_scan_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    // ── Pallets Created: one row per pallet made in the window (no live
    // rows — a pallet is created in one go). ──
    const palletCols: ColumnSpec<PalletCreatedRow>[] = [
        { key: "dashboard_col_pallet", kind: "text", value: (p) => p.pallet_sku, className: "dash-th-md" },
        { key: "dashboard_col_colour", kind: "text", value: (p) => p.colour },
        { key: "dashboard_col_type", kind: "text", value: (p) => p.pallet_type },
        { key: "dashboard_col_status", kind: "text", value: (p) => p.status },
        { key: "dashboard_col_user", kind: "text", value: (p) => p.user_name, className: "dash-th-user" },
        warehouseCol(),
        { key: "dashboard_col_created", kind: "number", value: (p) => timeMs(p.created_at) },
    ];
    const palletsView = () => completedSection(pallets.completed.length, (
        <div style={{ overflowX: "auto" }}>
            <table className="dash-table dash-table--aligned">
                {sortableHead(doneTable, palletCols)}
                <tbody>
                    {sorted(doneTable, pallets.completed, palletCols).map((p) => (
                        <tr key={p.pallet_sku}>
                            <td data-label={t("dashboard_col_pallet")}>{p.pallet_sku}</td>
                            <td data-label={t("dashboard_col_colour")}>{t(`colour_${p.colour.toLowerCase()}`)}</td>
                            <td data-label={t("dashboard_col_type")}>{p.pallet_type ?? "—"}</td>
                            <td data-label={t("dashboard_col_status")}>{t(`pallets_status_${p.status}`)}</td>
                            <td data-label={t("dashboard_col_user")}>{p.user_name ?? "—"}</td>
                            <td data-label={t("warehouse_col")}>{p.warehouse}</td>
                            <td data-label={t("dashboard_col_created")}>{clock(p.created_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ));

    // Goods In: session rows (a Book In tote or a BF Scan Sheet
    // session) — units booked + units/hr, per the aggregate design.
    type GoodsInRow = LiveGoodsInRow | CompletedGoodsInRow;
    const goodsInSourceLabel = (g: GoodsInRow) => {
        if (g.kind !== "bf_scan") return t("dashboard_source_book_in");
        return t(g.bf_mode === "big" ? "dashboard_source_bf_scan_big" : "dashboard_source_bf_scan");
    };
    const goodsInCols = <R extends GoodsInRow>(last: ColumnSpec<R>): ColumnSpec<R>[] => [
        { key: "dashboard_col_user", kind: "text", value: (g) => g.user_name, className: "dash-th-user" },
        { key: "dashboard_col_source", kind: "text", value: (g) => goodsInSourceLabel(g) },
        { key: "dashboard_col_ref", kind: "text", value: (g) => String(g.ref) },
        warehouseCol(),
        { key: "dashboard_col_units", kind: "number", value: (g) => g.units },
        { key: "dashboard_col_speed", kind: "number", value: (g) => g.speed_ipm },
        startCol(),
        last,
    ];
    const goodsInLiveCols = goodsInCols<LiveGoodsInRow>(lastActivityCol((g) => g.last_scan_at));
    const goodsInDoneCols = goodsInCols<CompletedGoodsInRow>(endCol());

    const goodsInCells = (g: LiveGoodsInRow | CompletedGoodsInRow) => (
        <>
            <td data-label={t("dashboard_col_user")} className="dash-td-primary">{g.user_name ?? "—"}</td>
            <td data-label={t("dashboard_col_source")}>{goodsInSourceLabel(g)}</td>
            <td data-label={t("dashboard_col_ref")}>{g.ref}</td>
            <td data-label={t("warehouse_col")}>{g.warehouse ?? "—"}</td>
            <td data-label={t("dashboard_col_units")}>{g.units}</td>
            {speedCell(g.speed_ipm)}
            {startCell(g.started_at)}
        </>
    );

    const goodsInLastActivityCell = (g: LiveGoodsInRow) => (
        <td data-label={t("dashboard_col_last_activity")}>
            {g.idle && <span className="dash-badge dash-badge--idle">{t("dashboard_idle")}</span>}
            {clock(g.last_scan_at)}
        </td>
    );

    const goodsInView = () => (
        <>
            {liveSection("dashboard_no_goods_in", goodsIn.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, goodsInLiveCols)}
                        <tbody>
                            {sorted(liveTable, goodsIn.live, goodsInLiveCols).map((g) => (
                                <tr key={`${g.kind}:${g.ref}`}>
                                    {goodsInCells(g)}
                                    {goodsInLastActivityCell(g)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(goodsIn.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, goodsInDoneCols)}
                        <tbody>
                            {sorted(doneTable, goodsIn.completed, goodsInDoneCols).map((g) => (
                                <tr key={`${g.kind}:${g.ref}:${g.ended_at}`}>
                                    {goodsInCells(g)}
                                    {endCell(g.ended_at)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    // Put Away: one session per person per destination TYPE. Cold rows
    // rate in totes/hr, hot/other rows in items/hr — the row's Type says
    // how to read it. Mis-slotted = tote category ≠ tier it went to.
    const putAwayTypeLabel = (type: PutAwayType) => t(`dashboard_putaway_type_${type}`);
    const putAwayRate = (type: PutAwayType, rate: number | null) => {
        if (rate === null) return "—";
        const n = String(Math.round(rate));
        return type === "cold" ? t("dashboard_rate_totes_ph", { n }) : t("dashboard_speed_iph", { n });
    };

    type PutAwayRow = LivePutAwayRow | CompletedPutAwayRow;
    const putAwayCols = <R extends PutAwayRow>(last: ColumnSpec<R>): ColumnSpec<R>[] => [
        { key: "dashboard_col_user", kind: "text", value: (r) => r.user_name, className: "dash-th-user" },
        warehouseCol(),
        { key: "dashboard_col_type", kind: "text", value: (r) => putAwayTypeLabel(r.type) },
        { key: "dashboard_col_totes_short", kind: "number", value: (r) => r.totes },
        { key: "dashboard_col_units", kind: "number", value: (r) => r.units },
        { key: "dashboard_col_speed", kind: "number", value: (r) => r.rate_ph },
        { key: "dashboard_col_misslotted", kind: "number", value: (r) => r.misslotted },
        startCol(),
        last,
    ];
    const putAwayLiveCols = putAwayCols<LivePutAwayRow>(lastActivityCol((r) => r.last_put_away_at));
    const putAwayDoneCols = putAwayCols<CompletedPutAwayRow>(endCol());

    const putAwayCells = (r: LivePutAwayRow | CompletedPutAwayRow) => (
        <>
            <td data-label={t("dashboard_col_user")} className="dash-td-primary">{r.user_name ?? "—"}</td>
            <td data-label={t("warehouse_col")}>{r.warehouse ?? "—"}</td>
            <td data-label={t("dashboard_col_type")}>{putAwayTypeLabel(r.type)}</td>
            <td data-label={t("dashboard_col_totes_short")}>{r.totes}</td>
            <td data-label={t("dashboard_col_units")}>{r.units}</td>
            <td data-label={t("dashboard_col_speed")}>{putAwayRate(r.type, r.rate_ph)}</td>
            <td data-label={t("dashboard_col_misslotted")}>{r.misslotted}</td>
            {startCell(r.started_at)}
        </>
    );

    const putAwayLastCell = (r: LivePutAwayRow) => (
        <td data-label={t("dashboard_col_last_activity")}>
            {r.idle && <span className="dash-badge dash-badge--idle">{t("dashboard_idle")}</span>}
            {clock(r.last_put_away_at)}
        </td>
    );

    const putAwayView = () => (
        <>
            {liveSection("dashboard_no_putaway", putaway.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, putAwayLiveCols)}
                        <tbody>
                            {sorted(liveTable, putaway.live, putAwayLiveCols).map((r) => (
                                <tr key={`${r.user_uid ?? r.user_name ?? "?"}:${r.type}:${r.started_at}`}>
                                    {putAwayCells(r)}
                                    {putAwayLastCell(r)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(putaway.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, putAwayDoneCols)}
                        <tbody>
                            {sorted(doneTable, putaway.completed, putAwayDoneCols).map((r) => (
                                <tr key={`${r.user_uid ?? r.user_name ?? "?"}:${r.type}:${r.started_at}`}>
                                    {putAwayCells(r)}
                                    {endCell(r.ended_at)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    // Dispatch: packer sessions — orders/units/parcels each with a
    // per-hour rate. Rates arrive per-hour from the BE (not ipm). A
    // session is one pick type (the BE splits on a type change, like a
    // new picking wave); orders that never went through a wave show
    // "Other".
    const perHour = (n: number | null) =>
        n !== null ? t("dashboard_rate_ph", { n: String(n) }) : "—";
    const dispatchPickTypeLabel = (pickType: DispatchPickType) =>
        pickType ? t(`picking_pick_type_${pickType}`) : t("dashboard_pick_type_other");

    type DispatchRow = LiveDispatchRow | CompletedDispatchRow;
    // Orders / units / parcels + their rates, shared by the Waves and
    // Summary dispatch tables.
    const dispatchCountCols = <R extends {
        orders: number; orders_ph: number | null; units: number; units_ph: number | null;
        parcels: number; parcels_ph: number | null;
    }>(): ColumnSpec<R>[] => [
        { key: "dashboard_col_orders", kind: "number", value: (d) => d.orders },
        { key: "dashboard_col_orders_ph", kind: "number", value: (d) => d.orders_ph },
        { key: "dashboard_col_units", kind: "number", value: (d) => d.units },
        { key: "dashboard_col_units_ph", kind: "number", value: (d) => d.units_ph },
        { key: "dashboard_col_parcels", kind: "number", value: (d) => d.parcels },
        { key: "dashboard_col_parcels_ph", kind: "number", value: (d) => d.parcels_ph },
    ];
    const dispatchCols = <R extends DispatchRow>(last: ColumnSpec<R>): ColumnSpec<R>[] => [
        { key: "dashboard_col_user", kind: "text", value: (d) => d.user_name, className: "dash-th-user" },
        { key: "picking_admin_pick_type_col", kind: "text", value: (d) => dispatchPickTypeLabel(d.pick_type), className: "dash-th-md" },
        warehouseCol(),
        ...dispatchCountCols<R>(),
        startCol(),
        last,
    ];
    const dispatchLiveCols = dispatchCols<LiveDispatchRow>(lastActivityCol((d) => d.last_packed_at));
    const dispatchDoneCols = dispatchCols<CompletedDispatchRow>(endCol());

    const dispatchCells = (d: LiveDispatchRow | CompletedDispatchRow) => (
        <>
            <td data-label={t("dashboard_col_user")} className="dash-td-primary">{d.user_name ?? "—"}</td>
            <td data-label={t("picking_admin_pick_type_col")}>{dispatchPickTypeLabel(d.pick_type)}</td>
            <td data-label={t("warehouse_col")}>{d.warehouse ?? "—"}</td>
            <td data-label={t("dashboard_col_orders")}>{d.orders}</td>
            <td data-label={t("dashboard_col_orders_ph")}>{perHour(d.orders_ph)}</td>
            <td data-label={t("dashboard_col_units")}>{d.units}</td>
            <td data-label={t("dashboard_col_units_ph")}>{perHour(d.units_ph)}</td>
            <td data-label={t("dashboard_col_parcels")}>{d.parcels}</td>
            <td data-label={t("dashboard_col_parcels_ph")}>{perHour(d.parcels_ph)}</td>
            {startCell(d.started_at)}
        </>
    );

    const dispatchLastPackCell = (d: LiveDispatchRow) => (
        <td data-label={t("dashboard_col_last_activity")}>
            {d.idle && <span className="dash-badge dash-badge--idle">{t("dashboard_idle")}</span>}
            {clock(d.last_packed_at)}
        </td>
    );

    const dispatchView = () => (
        <>
            {liveSection("dashboard_no_dispatch", dispatch.live.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(liveTable, dispatchLiveCols)}
                        <tbody>
                            {sorted(liveTable, dispatch.live, dispatchLiveCols).map((d) => (
                                <tr key={`${d.user_uid ?? d.user_name ?? "?"}:${d.pick_type ?? ""}:${d.started_at}`}>
                                    {dispatchCells(d)}
                                    {dispatchLastPackCell(d)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {completedSection(dispatch.completed.length, (
                <div style={{ overflowX: "auto" }}>
                    <table className="dash-table dash-table--aligned">
                        {sortableHead(doneTable, dispatchDoneCols)}
                        <tbody>
                            {sorted(doneTable, dispatch.completed, dispatchDoneCols).map((d) => (
                                <tr key={`${d.user_uid ?? d.user_name ?? "?"}:${d.pick_type ?? ""}:${d.started_at}`}>
                                    {dispatchCells(d)}
                                    {endCell(d.ended_at)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </>
    );

    // Department pills shared by Waves and Individual — same order, same
    // "coming soon" Put Away; only the count in brackets differs.
    const activityPills = (countOf: (key: Activity) => number | null) => (
        <div className="dash-activity-row" role="tablist">
            {activities.map(({ key, labelKey, enabled }) => {
                const count = countOf(key);
                return (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={activity === key}
                        disabled={!enabled}
                        title={enabled ? undefined : t("coming_soon")}
                        className={`stock-alloc-pill${activity === key ? " stock-alloc-pill--active" : ""}`}
                        onClick={() => enabled && setActivity(key)}
                    >
                        {t(labelKey)}
                        {enabled && count !== null && loaded && ` (${count})`}
                        {!enabled && <span className="dash-pill-soon">{t("coming_soon")}</span>}
                    </button>
                );
            })}
        </div>
    );

    // ── Summary view (scope "individual"; pill renamed Summary, Alex
    // 2026-09-09 — state key + i18n key kept) ────────────────────────
    // One department at a time (same pills as Waves), one line per
    // person (per pick type for picking and dispatch — a person's lines
    // sit together), over today's live + completed work. Time spent =
    // the same minutes the speeds are computed over.
    const individualCountFor = (key: Activity): number | null => payloadFor(key).by_user.length;
    const fmtMinutes = (min: number) => {
        const total = Math.round(min);
        const h = Math.floor(total / 60);
        const m = total % 60;
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };
    const speedText = (ipm: number | null) =>
        ipm !== null ? t("dashboard_speed_iph", { n: String(Math.round(ipm * 60)) }) : "—";
    const cell = (labelKey: string, value: React.ReactNode, primary = false) => (
        <td data-label={t(labelKey)} className={primary ? "dash-td-primary" : undefined}>{value}</td>
    );
    // Summary tables: rows come from the by_user arrays; header + order
    // share one ColumnSpec list per department (sortable like Waves).
    const summaryTable = <R,>(rows: R[], cols: ColumnSpec<R>[], row: (r: R) => React.ReactNode) =>
        rows.length === 0 ? emptyState("dashboard_no_individual") : (
            <div style={{ overflowX: "auto" }}>
                <table className="dash-table">
                    {sortableHead(summaryTableId, cols)}
                    <tbody>{sorted(summaryTableId, rows, cols).map(row)}</tbody>
                </table>
            </div>
        );
    const userCol = <R extends { user_name: string | null }>(): ColumnSpec<R> =>
        ({ key: "dashboard_col_user", kind: "text", value: (r) => r.user_name });
    const numCol = <R,>(key: string, value: (r: R) => number | null): ColumnSpec<R> =>
        ({ key, kind: "number", value });
    const textCol = <R,>(key: string, value: (r: R) => string | null): ColumnSpec<R> =>
        ({ key, kind: "text", value });
    const unitsCol = <R extends { units: number }>(): ColumnSpec<R> =>
        numCol<R>("dashboard_col_units", (r) => r.units);
    const speedCol = <R extends { speed_ipm: number | null }>(): ColumnSpec<R> =>
        numCol<R>("dashboard_col_speed", (r) => r.speed_ipm);
    const minutesCol = <R extends { minutes: number }>(): ColumnSpec<R> =>
        numCol<R>("dashboard_col_time_spent", (r) => r.minutes);

    const goodsInSummaryCols: ColumnSpec<GoodsInByUser>[] = [
        userCol(), numCol("dashboard_col_sessions", (r) => r.sessions), unitsCol(),
        speedCol(), minutesCol(), numCol("dashboard_col_totes", (r) => r.totes),
    ];
    const putAwaySummaryCols: ColumnSpec<PutAwayByUser>[] = [
        userCol(), textCol("dashboard_col_type", (r) => putAwayTypeLabel(r.type)),
        numCol("dashboard_col_sessions", (r) => r.sessions), numCol("dashboard_col_totes_short", (r) => r.totes),
        unitsCol(), numCol("dashboard_col_speed", (r) => r.rate_ph), minutesCol(),
        numCol("dashboard_col_misslotted", (r) => r.misslotted),
    ];
    const pickSummaryCols: ColumnSpec<PickingByUser>[] = [
        userCol(), textCol("picking_admin_pick_type_col", (r) => t(`picking_pick_type_${r.pick_type}`)),
        numCol("dashboard_col_waves", (r) => r.waves), unitsCol(), speedCol(), minutesCol(),
    ];
    const dropSummaryCols: ColumnSpec<ShelfDropByUser>[] = [
        userCol(), numCol("dashboard_col_waves", (r) => r.sessions), unitsCol(), speedCol(), minutesCol(),
    ];
    const sortSummaryCols: ColumnSpec<SortingByUser>[] = [
        userCol(), numCol("dashboard_col_waves", (r) => r.waves), unitsCol(), speedCol(), minutesCol(),
    ];
    const dispatchSummaryCols: ColumnSpec<DispatchByUser>[] = [
        userCol(), textCol("picking_admin_pick_type_col", (r) => dispatchPickTypeLabel(r.pick_type)),
        ...dispatchCountCols<DispatchByUser>(), minutesCol(),
    ];

    const sorterSummaryCols: ColumnSpec<SorterByUser>[] = [
        userCol(), numCol("dashboard_col_sessions", (r) => r.sessions), unitsCol(), speedCol(), minutesCol(),
    ];
    const palletsSummaryCols: ColumnSpec<PalletsByUser>[] = [
        userCol(), numCol("dashboard_col_pallets", (r) => r.pallets),
        numCol("colour_red", (r) => r.red), numCol("colour_yellow", (r) => r.yellow),
        numCol("colour_grey", (r) => r.grey), numCol("colour_black", (r) => r.black),
    ];

    const individualView = () => (
        <>
            {activityPills(individualCountFor)}
            {activity === "goods_in" && summaryTable(goodsIn.by_user, goodsInSummaryCols, (r) => (
                <tr key={String(r.user_uid ?? r.user_name)}>
                    {cell("dashboard_col_user", r.user_name ?? "—", true)}
                    {cell("dashboard_col_sessions", r.sessions)}
                    {cell("dashboard_col_units", r.units)}
                    {cell("dashboard_col_speed", speedText(r.speed_ipm))}
                    {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                    {cell("dashboard_col_totes", r.totes)}
                </tr>
            ))}
            {activity === "putaway" && summaryTable(putaway.by_user, putAwaySummaryCols, (r) => (
                <tr key={`${String(r.user_uid ?? r.user_name)}:${r.type}`}>
                    {cell("dashboard_col_user", r.user_name ?? "—", true)}
                    {cell("dashboard_col_type", putAwayTypeLabel(r.type))}
                    {cell("dashboard_col_sessions", r.sessions)}
                    {cell("dashboard_col_totes_short", r.totes)}
                    {cell("dashboard_col_units", r.units)}
                    {cell("dashboard_col_speed", putAwayRate(r.type, r.rate_ph))}
                    {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                    {cell("dashboard_col_misslotted", r.misslotted)}
                </tr>
            ))}
            {activity === "pick_face" && summaryTable(picks.by_user, pickSummaryCols, (r) => (
                <tr key={`${String(r.user_uid ?? r.user_name)}:${r.pick_type}`}>
                    {cell("dashboard_col_user", r.user_name ?? "—", true)}
                    {cell("picking_admin_pick_type_col", t(`picking_pick_type_${r.pick_type}`))}
                    {cell("dashboard_col_waves", r.waves)}
                    {cell("dashboard_col_units", r.units)}
                    {cell("dashboard_col_speed", speedText(r.speed_ipm))}
                    {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                </tr>
            ))}
            {(activity === "shelf_drop" || activity === "forecast_drop") && summaryTable(
                (activity === "shelf_drop" ? drops : forecast).by_user, dropSummaryCols, (r) => (
                    <tr key={String(r.user_uid ?? r.user_name)}>
                        {cell("dashboard_col_user", r.user_name ?? "—", true)}
                        {cell("dashboard_col_waves", r.sessions)}
                        {cell("dashboard_col_units", r.units)}
                        {cell("dashboard_col_speed", speedText(r.speed_ipm))}
                        {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                    </tr>
                ))}
            {activity === "sorting" && summaryTable(sorts.by_user, sortSummaryCols, (r) => (
                <tr key={String(r.user_uid ?? r.user_name)}>
                    {cell("dashboard_col_user", r.user_name ?? "—", true)}
                    {cell("dashboard_col_waves", r.waves)}
                    {cell("dashboard_col_units", r.units)}
                    {cell("dashboard_col_speed", speedText(r.speed_ipm))}
                    {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                </tr>
            ))}
            {activity === "dispatch" && summaryTable(dispatch.by_user, dispatchSummaryCols, (r) => (
                <tr key={`${String(r.user_uid ?? r.user_name)}:${r.pick_type ?? ""}`}>
                    {cell("dashboard_col_user", r.user_name ?? "—", true)}
                    {cell("picking_admin_pick_type_col", dispatchPickTypeLabel(r.pick_type))}
                    {cell("dashboard_col_orders", r.orders)}
                    {cell("dashboard_col_orders_ph", perHour(r.orders_ph))}
                    {cell("dashboard_col_units", r.units)}
                    {cell("dashboard_col_units_ph", perHour(r.units_ph))}
                    {cell("dashboard_col_parcels", r.parcels)}
                    {cell("dashboard_col_parcels_ph", perHour(r.parcels_ph))}
                    {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                </tr>
            ))}
            {(activity === "sorter_1" || activity === "sorter_2") && summaryTable(
                (activity === "sorter_1" ? sorter1 : sorter2).by_user, sorterSummaryCols, (r) => (
                    <tr key={String(r.user_uid ?? r.user_name)}>
                        {cell("dashboard_col_user", r.user_name ?? "—", true)}
                        {cell("dashboard_col_sessions", r.sessions)}
                        {cell("dashboard_col_units", r.units)}
                        {cell("dashboard_col_speed", speedText(r.speed_ipm))}
                        {cell("dashboard_col_time_spent", fmtMinutes(r.minutes))}
                    </tr>
                ))}
            {activity === "pallets_created" && summaryTable(pallets.by_user, palletsSummaryCols, (r) => (
                <tr key={String(r.user_uid ?? r.user_name)}>
                    {cell("dashboard_col_user", r.user_name ?? "—", true)}
                    {cell("dashboard_col_pallets", r.pallets)}
                    {cell("colour_red", r.red)}
                    {cell("colour_yellow", r.yellow)}
                    {cell("colour_grey", r.grey)}
                    {cell("colour_black", r.black)}
                </tr>
            ))}
        </>
    );

    // superAdmin: the full dropdown. Everyone else: a static label of
    // the warehouse they're locked to (the BE enforces the lock). One
    // pill-styled control in the shared toggle row for BOTH scopes (per
    // Alex, 2026-08-26 — Individual used to carry its own in the pills row).
    const warehouseControl = isSuperAdmin ? (
        <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="dash-pill-select"
        >
            <option value="">{t("picking_admin_all_warehouses")}</option>
            {warehouses.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
    ) : (
        <span className="dash-wh-locked">{user.warehouse ?? "—"}</span>
    );

    // Date range — preset pill + from/to date pills, right-aligned in
    // the toggle row. Editing a date flips to Custom. Team uses it for
    // the KPI cards; Waves and Summary for their history window.
    const rangeControl = (
        preset: KpiPreset,
        value: KpiRange,
        presets: KpiPreset[],
        onPreset: (p: KpiPreset) => void,
        onChange: (r: KpiRange, edited: "from" | "to") => void,
        maxDate?: string,
    ) => (
        <div className="dash-scope-right">
            <select
                className="dash-pill-select"
                value={preset}
                onChange={(e) => onPreset(e.target.value as KpiPreset)}
                aria-label={t("dashboard_team_range_label")}
            >
                {presets.map((p) => (
                    <option key={p} value={p}>{t(PRESET_LABEL_KEY[p])}</option>
                ))}
            </select>
            <input
                type="date"
                className="dash-pill-input"
                value={value.from}
                max={maxDate && maxDate < value.to ? maxDate : value.to}
                onChange={(e) => e.target.value && onChange({ ...value, from: e.target.value }, "from")}
                aria-label={t("dashboard_team_range_from")}
            />
            <span className="dash-pill-sep">–</span>
            <input
                type="date"
                className="dash-pill-input"
                value={value.to}
                min={value.from}
                max={maxDate}
                onChange={(e) => e.target.value && onChange({ ...value, to: e.target.value }, "to")}
                aria-label={t("dashboard_team_range_to")}
            />
        </div>
    );
    const kpiRangeControl = rangeControl(kpiPreset, kpiRange, KPI_PRESETS, onKpiPreset, onKpiRange);
    // History can't start after today (the BE rejects it) — cap both pickers.
    const activityRangeControl = rangeControl(rangePreset, range, ACTIVITY_PRESETS, onRangePreset, onRangeChange, today);

    // One shared toggle row for both scopes — Team first and the
    // default landing view. On Team the warehouse filter sits right of
    // the toggle (the page has no header of its own); on Individual it
    // stays in the activity-pills row as before.
    return (
        <div>
            <div className="dash-scope-row">
                <div className="dash-scope-toggle" role="tablist" aria-label={t("dashboard_scope_toggle")}>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "team"}
                        className={`dash-scope-btn${scope === "team" ? " dash-scope-btn--active" : ""}`}
                        onClick={() => setScope("team")}
                    >
                        <Users size={14} />
                        {t("dashboard_scope_team")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "waves"}
                        className={`dash-scope-btn${scope === "waves" ? " dash-scope-btn--active" : ""}`}
                        onClick={() => setScope("waves")}
                    >
                        <Waves size={14} />
                        {t("dashboard_scope_waves")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "individual"}
                        className={`dash-scope-btn${scope === "individual" ? " dash-scope-btn--active" : ""}`}
                        onClick={() => setScope("individual")}
                    >
                        <User size={14} />
                        {t("dashboard_scope_individual")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "planner"}
                        className={`dash-scope-btn${scope === "planner" ? " dash-scope-btn--active" : ""}`}
                        onClick={() => setScope("planner")}
                    >
                        <Calculator size={14} />
                        {t("dashboard_scope_planner")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "leaders_bonus"}
                        className={`dash-scope-btn${scope === "leaders_bonus" ? " dash-scope-btn--active" : ""}`}
                        onClick={() => setScope("leaders_bonus")}
                    >
                        <Trophy size={14} />
                        {t("dashboard_scope_leaders_bonus")}
                    </button>
                </div>
                {warehouseControl}
                {scope === "team" && updatedLabel && (
                    <span className="dash-updated" aria-live="polite">{updatedLabel}</span>
                )}
                {scope === "planner" || scope === "leaders_bonus" ? null : scope === "team" ? kpiRangeControl : activityRangeControl}
            </div>

            {scope === "planner" && <DashboardPlanner warehouseFilter={warehouseFilter} />}
            {scope === "leaders_bonus" && <DashboardLeadersBonus warehouseFilter={warehouseFilter} />}

            {scope === "team" && (
                <DashboardTeam
                    team={team}
                    loaded={teamLoaded}
                    layout={layout}
                    kpis={kpis}
                    kpisLoaded={kpisLoaded}
                    week={week}
                    weekLoaded={weekLoaded}
                    shippedByDate={showsShippedByDate(warehouseFilter)
                        ? <DashboardShippedByDate warehouse={warehouseFilter} />
                        : undefined}
                />
            )}
            {scope === "individual" && individualView()}
            {scope === "waves" && (<>
            {activityPills(countFor)}

            {activity === "goods_in" && goodsInView()}
            {activity === "putaway" && putAwayView()}
            {activity === "pick_face" && pickingView()}
            {activity === "forecast_drop" && dropView(forecast, "dashboard_no_forecast_drops")}
            {activity === "shelf_drop" && dropView(drops, "dashboard_no_shelf_drops")}
            {activity === "sorting" && sortingView()}
            {activity === "dispatch" && dispatchView()}
            {activity === "sorter_1" && sorterView(sorter1)}
            {activity === "sorter_2" && sorterView(sorter2)}
            {activity === "pallets_created" && palletsView()}
            </>)}
        </div>
    );
};

export default DashboardAdmin;
